/**
 * ProviderSwitchModal Component
 *
 * Modal displayed when user switches active sync provider via dropdown.
 * Handles two scenarios:
 * 1. Previous connection exists: Offer reconnect or new connection
 * 2. No previous connection: Prompt to connect now
 *
 * Features:
 * - Provider-specific messaging (Google Drive vs Local Directory)
 * - Previous connection details display (email, directory name)
 * - Mobile-responsive layout
 * - Full accessibility (WCAG 2.1 AA)
 * - Focus trapping within modal
 *
 * @example
 * ```tsx
 * const [isOpen, setIsOpen] = useState(false);
 * const [choice, setChoice] = useState<'reconnect' | 'new' | 'cancel' | null>(null);
 *
 * <ProviderSwitchModal
 *   isOpen={isOpen}
 *   onOpenChange={setIsOpen}
 *   variant="reconnect"
 *   providerKey="google-drive"
 *   previousConnection={{ email: "user@example.com" }}
 *   onChoice={setChoice}
 * />
 * ```
 */

import React, { useState } from 'react';

// Type imports
import type { ProviderKey } from '@/types/sync';

// Utility imports
import { openOAuthPopup } from '@/utils';
import { Brand } from '@enfomentis/shared/config';

// UI Component imports
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from '@enfomentis/ui';
import { Button } from '@enfomentis/ui';
import { Alert } from '@enfomentis/ui';

/**
 * User's choice from the modal
 */
export type ProviderSwitchChoice = 'reconnect' | 'new' | 'cancel';

/**
 * Extended choice data including pre-opened OAuth popup for cloud providers.
 * The popup is opened synchronously on button click to avoid browser popup blocking.
 */
export interface ProviderSwitchChoiceData {
  choice: ProviderSwitchChoice;
  /**
   * Pre-opened popup window for OAuth flow.
   * Opened synchronously on button click to avoid browser popup blocking.
   * Only set for 'reconnect' or 'new' choices with cloud providers (google-drive).
   */
  oauthPopup?: Window | null;
}

/**
 * Previous connection metadata
 */
export interface PreviousConnectionInfo {
  /** Email address (Google Drive only) */
  email?: string | null;
  /** Directory name (Local Directory only) */
  directoryName?: string | null;
}

/**
 * Props for ProviderSwitchModal component
 */
export interface ProviderSwitchModalProps {
  /** Whether the modal is currently open */
  isOpen: boolean;
  /** Callback when modal open state changes */
  onOpenChange: (open: boolean) => void;
  /** Modal variant: "reconnect" (has prior connection) or "connect" (first time) */
  variant: 'reconnect' | 'connect';
  /** Provider key being switched to */
  providerKey: ProviderKey;
  /** Previous connection info (for "reconnect" variant) */
  previousConnection?: PreviousConnectionInfo | undefined;
  /**
   * Callback when user makes a choice.
   * For cloud providers (google-drive), includes pre-opened OAuth popup to avoid browser blocking.
   */
  onChoice: (choiceData: ProviderSwitchChoiceData) => void;
}

/**
 * Helper function to get provider display name from ProviderKey.
 *
 * @param providerKey - The provider key to convert
 * @returns Human-readable provider name
 */
function getProviderDisplayName(providerKey: ProviderKey): string {
  switch (providerKey) {
    case 'google-drive':
      return 'Google Drive';
    case 'local':
      return 'Local Directory';
    default: {
      // Exhaustiveness check
      const _exhaustive: never = providerKey;
      console.warn('[ProviderSwitchModal] Unknown provider:', _exhaustive);
      return 'Unknown Provider';
    }
  }
}

/**
 * ProviderSwitchModal Component
 *
 * Modal for handling provider switching with reconnect or new connection options.
 */
export function ProviderSwitchModal(props: ProviderSwitchModalProps): React.ReactElement {
  const {
    isOpen,
    onOpenChange,
    variant,
    providerKey,
    previousConnection,
    onChoice,
  } = props;

  const providerDisplayName = getProviderDisplayName(providerKey);

  // ===== STATE =====

  const [popupBlocked, setPopupBlocked] = useState<boolean>(false);

  // ===== EVENT HANDLERS =====

  const handleReconnect = (): void => {
    // Open popup FIRST (synchronous, preserves user gesture)
    const oauthPopup = openOAuthPopup(providerKey);

    // Guard: popup blocked for cloud provider
    if (providerKey === 'google-drive' && !oauthPopup) {
      setPopupBlocked(true);
      return;
    }

    // Clear any previous popup blocked state and proceed
    setPopupBlocked(false);
    onChoice({ choice: 'reconnect', oauthPopup });
    onOpenChange(false);
  };

  const handleNew = (): void => {
    // Open popup FIRST (synchronous, preserves user gesture)
    const oauthPopup = openOAuthPopup(providerKey);

    // Guard: popup blocked for cloud provider
    if (providerKey === 'google-drive' && !oauthPopup) {
      setPopupBlocked(true);
      return;
    }

    // Clear any previous popup blocked state and proceed
    setPopupBlocked(false);
    onChoice({ choice: 'new', oauthPopup });
    onOpenChange(false);
  };

  const handleCancel = (): void => {
    setPopupBlocked(false);
    onChoice({ choice: 'cancel' });
    onOpenChange(false);
  };

  const handleClose = (): void => {
    // Modal close via backdrop/ESC key treated as cancel
    setPopupBlocked(false);
    onChoice({ choice: 'cancel' });
    onOpenChange(false);
  };

  // ===== RENDER HELPERS =====

  /**
   * Render title based on variant and provider
   */
  const renderTitle = (): string => {
    if (variant === 'reconnect') {
      return `Reconnect to ${providerDisplayName}?`;
    }
    return `Connect to ${providerDisplayName}?`;
  };

  /**
   * Render body content based on variant
   */
  const renderBody = (): React.ReactElement => {
    if (variant === 'reconnect') {
      return (
        <div className="flex flex-col gap-4">
          {/* Popup Blocked Warning */}
          {popupBlocked && (
            <Alert
              color="warning"
              variant="flat"
              title="Popup Blocked"
              description="Please allow popups for this site and try again."
            />
          )}

          <p className="text-sm text-text-secondary">
            We found a previous {providerDisplayName} connection. Would you like to reconnect or start fresh?
          </p>

          {/* Previous connection details */}
          {previousConnection && (
            <Alert
              color="primary"
              variant="flat"
              title="Previous Connection"
              description={
                providerKey === 'google-drive' && previousConnection.email
                  ? previousConnection.email
                  : providerKey === 'local' && previousConnection.directoryName
                    ? previousConnection.directoryName
                    : 'Connection details not available'
              }
            />
          )}

          {/* Explanation of options */}
          <div className="text-sm space-y-2">
            <p className="font-medium">Your options:</p>
            <ul className="list-disc list-inside space-y-1 text-text-secondary ml-2">
              <li>
                <strong>Reconnect</strong>: Use your previous {providerKey === 'google-drive' ? 'account and folder' : 'directory'}
              </li>
              <li>
                <strong>Connect to Different {providerKey === 'google-drive' ? 'Account' : 'Directory'}</strong>:
                {providerKey === 'google-drive'
                  ? ' Choose a different Google account or folder'
                  : ' Select a different directory on your device'}
              </li>
            </ul>
          </div>
        </div>
      );
    }

    // variant === 'connect'
    return (
      <div className="flex flex-col gap-4">
        {/* Popup Blocked Warning */}
        {popupBlocked && (
          <Alert
            color="warning"
            variant="flat"
            title="Popup Blocked"
            description="Please allow popups for this site and try again."
          />
        )}

        <p className="text-sm text-text-secondary">
          {providerKey === 'google-drive'
            ? 'Connect your Google Drive account to sync your trading journal data across devices.'
            : 'Select a local directory on your device to sync your trading journal data.'}
        </p>

        {providerKey === 'google-drive' && (
          <Alert
            color="primary"
            variant="flat"
            title="First Time Setup"
            description={`You'll be asked to authorize ${Brand.displayName} to access a dedicated folder in your Google Drive. Your trading data stays private and is only accessible by you.`}
          />
        )}

        {providerKey === 'local' && (
          <Alert
            color="warning"
            variant="flat"
            title="Local Directory Sync"
            description="Choose an empty directory or one that was previously used for sync. Selecting a directory with unrelated files may cause conflicts."
          />
        )}
      </div>
    );
  };

  /**
   * Render footer buttons based on variant
   */
  const renderFooter = (): React.ReactElement => {
    if (variant === 'reconnect') {
      return (
        <>
          {/* Cancel button (left side on desktop, bottom on mobile) */}
          <Button
            variant="flat"
            color="default"
            onPress={handleCancel}
            className="order-3 md:order-1"
          >
            Cancel
          </Button>

          {/* Action buttons (right side on desktop, top on mobile) */}
          <div className="flex flex-col sm:flex-row gap-2 order-1 md:order-2">
            <Button
              variant="flat"
              color="primary"
              onPress={handleNew}
            >
              Connect to Different {providerKey === 'google-drive' ? 'Account' : 'Directory'}
            </Button>
            <Button
              variant="solid"
              color="primary"
              onPress={handleReconnect}
            >
              Reconnect to Previous
            </Button>
          </div>
        </>
      );
    }

    // variant === 'connect'
    return (
      <>
        <Button
          variant="flat"
          color="default"
          onPress={handleCancel}
        >
          Cancel
        </Button>
        <Button
          variant="solid"
          color="primary"
          onPress={handleNew}
        >
          Connect Now
        </Button>
      </>
    );
  };

  // ===== MAIN RENDER =====

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={handleClose}
      isDismissable={true}
      size="lg"
      role="dialog"
      aria-labelledby="provider-switch-modal-title"
      aria-describedby="provider-switch-modal-body"
      placement="center"
    >
      <ModalContent>
        <ModalHeader id="provider-switch-modal-title">
          {renderTitle()}
        </ModalHeader>
        <ModalBody id="provider-switch-modal-body">
          {renderBody()}
        </ModalBody>
        <ModalFooter className="flex flex-col md:flex-row md:justify-between gap-2">
          {renderFooter()}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
